import { useState, useEffect } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { GuardedDialog, isDirty } from "@/components/support/GuardedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LOCATION_TYPES, BILLING_STATUSES, PRIORITIES, fmtDuration, persistedIdemKey, clearIdemKey } from "@/lib/supportApi";
import { toast } from "sonner";

export function ReviewDialog({ open, onOpenChange, session, onReviewed }) {
  const { currentOrgId } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [machineCustomerId, setMachineCustomerId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});
  const [init, setInit] = useState({});

  useEffect(() => {
    if (!open || !session || !currentOrgId) return;
    const v = {
      work_description: "", notes: "", category_key: "remote_support", location_type: "remote",
      billing_status: "unclassified", minutes: Math.round((session.duration_seconds || 0) / 60),
      create_time_entry: true, customer_id: session.customer_id || "", apply_to_machine: false,
      ticket_mode: "none", ticket_id: "", new_subject: "", new_priority: "Normal",
    };
    setF(v); setInit(v);
    api.get(`/support/categories?org_id=${currentOrgId}`).then((r) => setCategories(r.data)).catch(() => {});
    if (!session.customer_id) {
      api.get(`/support/customers?org_id=${currentOrgId}&status=active`).then((r) => setCustomers(r.data)).catch(() => {});
      api.get(`/support/machines?org_id=${currentOrgId}`).then((r) => {
        const m = (r.data || []).find((x) => x.machine_id === session.machine_id);
        setMachineCustomerId(m?.customer_id || null);
      }).catch(() => setMachineCustomerId(null));
    }
  }, [open, session, currentOrgId]);

  useEffect(() => {
    const cid = f.customer_id || session?.customer_id;
    if (cid && currentOrgId) api.get(`/support/tickets?org_id=${currentOrgId}&customer_id=${cid}`).then((r) => setTickets(r.data)).catch(() => {});
  }, [f.customer_id, session, currentOrgId]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // Default checked only when the machine is currently unassigned; never pre-check a reassignment.
  const pickCustomer = (v) => setF((p) => ({ ...p, customer_id: v, apply_to_machine: !machineCustomerId }));
  if (!session) return null;

  const submit = async () => {
    if (!session.customer_id && !f.customer_id) { toast.error("Assign a customer first"); return; }
    setSaving(true);
    const scope = `review:${session.session_id}`;
    const body = {
      org_id: currentOrgId, idempotency_key: persistedIdemKey(scope),
      work_description: f.work_description, notes: f.notes, category_key: f.category_key,
      location_type: f.location_type, billing_status: f.billing_status,
      actual_seconds: Math.round(parseFloat(f.minutes || "0") * 60),
      customer_id: f.customer_id || null, create_time_entry: !!f.create_time_entry,
      apply_to_machine: !session.customer_id && !!f.customer_id && !!f.apply_to_machine,
      ticket_id: f.ticket_mode === "existing" ? (f.ticket_id || null) : null,
      new_ticket: f.ticket_mode === "new" ? { subject: f.new_subject || "Remote session", priority: f.new_priority } : null,
    };
    try {
      await api.post(`/support/sessions/${session.session_id}/review`, body);
      clearIdemKey(scope);
      toast.success("Session reviewed");
      onOpenChange(false);
      onReviewed && onReviewed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save review");
    } finally { setSaving(false); }
  };

  return (
    <GuardedDialog open={open} onOpenChange={onOpenChange} dirty={isDirty(f, init)} className="bg-zinc-900 border-white/10 max-w-lg" testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Review remote session</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {session.machine_name} · connection {fmtDuration(session.duration_seconds)}. Confirm the actual working time (separate from connection time).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[65vh] overflow-y-auto">
          {!session.customer_id && (
            <div>
              <Label className="text-xs text-amber-400">No customer on this machine — assign one</Label>
              <Select value={f.customer_id} onValueChange={pickCustomer}>
                <SelectTrigger data-testid="review-customer-select" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {f.customer_id && (
                <label className="mt-2 flex items-start gap-2 text-xs text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="review-apply-to-machine"
                    checked={!!f.apply_to_machine}
                    onChange={(e) => set("apply_to_machine", e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 accent-blue-600"
                  />
                  <span>
                    Also assign this computer to this customer permanently.
                    {machineCustomerId && <span className="block text-amber-400 mt-0.5">This computer is already assigned to another customer — ticking this will reassign it.</span>}
                  </span>
                </label>
              )}
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/40 px-3 py-2">
            <div>
              <div className="text-sm font-medium">Create a time entry</div>
              <div className="text-xs text-zinc-500">Off = keep session history only, no labour logged</div>
            </div>
            <Switch data-testid="review-create-entry-switch" checked={!!f.create_time_entry} onCheckedChange={(v) => set("create_time_entry", v)} />
          </div>
          {f.create_time_entry && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Actual working minutes</Label>
                  <Input data-testid="review-minutes-input" type="number" min="0" value={f.minutes} onChange={(e) => set("minutes", e.target.value)} className="mt-1 bg-black border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Billing</Label>
                  <Select value={f.billing_status} onValueChange={(v) => set("billing_status", v)}>
                    <SelectTrigger data-testid="review-billing-select" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {BILLING_STATUSES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Category</Label>
                  <Select value={f.category_key} onValueChange={(v) => set("category_key", v)}>
                    <SelectTrigger className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {categories.map((c) => <SelectItem key={c.category_key} value={c.category_key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Location</Label>
                  <Select value={f.location_type} onValueChange={(v) => set("location_type", v)}>
                    <SelectTrigger className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {LOCATION_TYPES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs text-zinc-400">Ticket</Label>
            <Select value={f.ticket_mode} onValueChange={(v) => set("ticket_mode", v)}>
              <SelectTrigger data-testid="review-ticket-mode" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="none">No ticket</SelectItem>
                <SelectItem value="existing">Link existing ticket</SelectItem>
                <SelectItem value="new">Create new ticket</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.ticket_mode === "existing" && (
            <Select value={f.ticket_id} onValueChange={(v) => set("ticket_id", v)}>
              <SelectTrigger className="bg-black border-zinc-800"><SelectValue placeholder="Choose ticket" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {tickets.map((t) => <SelectItem key={t.ticket_id} value={t.ticket_id}>#{t.ticket_number} {t.subject}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {f.ticket_mode === "new" && (
            <div className="grid grid-cols-3 gap-2">
              <Input className="col-span-2 bg-black border-zinc-800" placeholder="New ticket subject" value={f.new_subject} onChange={(e) => set("new_subject", e.target.value)} />
              <Select value={f.new_priority} onValueChange={(v) => set("new_priority", v)}>
                <SelectTrigger className="bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-zinc-400">Work description</Label>
            <Textarea data-testid="review-description-input" value={f.work_description} onChange={(e) => set("work_description", e.target.value)} className="mt-1 bg-black border-zinc-800" rows={2} />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Notes (optional)</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 bg-black border-zinc-800" rows={2} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <DialogClose asChild><Button variant="ghost" data-testid="review-later-button" className="text-zinc-400">Review later</Button></DialogClose>
          <Button data-testid="review-submit-button" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
            {saving ? "Saving…" : "Save review"}
          </Button>
        </DialogFooter>
    </GuardedDialog>
  );
}
